import React from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';

import { FormSection } from '@components/form-section';
import { ImageUpload } from '@components/image-upload';
import { PageHeader } from '@components/page-header';
import { Select } from '@components/select';
import { StickyBar } from '@components/sticky-bar';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { extractApiErrorMessage } from '@core/utils/errors';
import { formatEnumLabel } from '@core/utils/format-enum-label';
import {
    TeamAccess,
    TeamLeaderboardVisibility,
    TeamMemberVisibility
} from '@api/ChallengeClient';

import { useCreateTeamStore } from './create-team-store';

const enumOptions = <T extends string>(values: T[]) =>
    values.map((value) => ({ label: formatEnumLabel(value), value }));

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p className="text-xs text-muted-foreground">{children}</p>
);

const CreateTeamForm = observer(({ communityId }: { communityId: number }) => {
    const {
        fields,
        onSubmitHandler,
        onCancelHandler,
        createRequest,
        imageUpload
    } = useCreateTeamStore(communityId);

    const submitDisabled = createRequest.loading || imageUpload.loading;

    return (
        <>
            <PageHeader title="Create Team" />

            <div className="flex flex-col gap-6 pb-24">
                <FormSection title="Basic Information">
                    <div className="grid gap-4 md:grid-cols-2">
                        <TextField
                            label="Team Name"
                            type="text"
                            value={fields.name.value}
                            onChange={(value) =>
                                fields.name.onChange(String(value))
                            }
                            error={fields.name.error}
                            required
                        />
                        <div className="md:col-span-2">
                            <TextField
                                label="Description"
                                type="text"
                                value={fields.description.value}
                                onChange={(value) =>
                                    fields.description.onChange(String(value))
                                }
                                error={fields.description.error}
                                required
                                multiline
                            />
                        </div>
                        <div className="md:col-span-2">
                            <ImageUpload
                                onUpload={imageUpload.handleUpload}
                                loading={imageUpload.loading}
                                error={imageUpload.error}
                                previewUrl={imageUpload.previewUrl}
                                label="Team Image"
                                helperText="Drag and drop a team image here, or click to select"
                            />
                            {(fields.image.bucketName.error ||
                                fields.image.objectKey.error) && (
                                <Alert variant="destructive" className="mt-4">
                                    <AlertDescription>
                                        {fields.image.bucketName.error ||
                                            fields.image.objectKey.error}
                                    </AlertDescription>
                                </Alert>
                            )}
                        </div>
                    </div>
                </FormSection>

                <FormSection title="Manager">
                    <div className="flex flex-col gap-1.5 md:w-2/3">
                        <TextField
                            label="Manager User ID"
                            type="text"
                            value={fields.managerUserId.value}
                            onChange={(value) =>
                                fields.managerUserId.onChange(String(value))
                            }
                            error={fields.managerUserId.error}
                            required
                        />
                        {!fields.managerUserId.error && (
                            <Hint>
                                Paste the user ID. The selected user will be
                                added as a team member.
                            </Hint>
                        )}
                    </div>
                </FormSection>

                <FormSection title="Access & Visibility">
                    <div className="grid gap-4 md:grid-cols-3">
                        <Select
                            label="Access"
                            required
                            value={fields.access.value}
                            options={enumOptions(Object.values(TeamAccess))}
                            onChange={(value: TeamAccess) =>
                                fields.access.onChange(value)
                            }
                        />
                        <Select
                            label="Member Visibility"
                            required
                            value={fields.memberVisibility.value}
                            options={enumOptions(
                                Object.values(TeamMemberVisibility)
                            )}
                            onChange={(value: TeamMemberVisibility) =>
                                fields.memberVisibility.onChange(value)
                            }
                        />
                        <Select
                            label="Leaderboard Visibility"
                            required
                            value={fields.leaderboardVisibility.value}
                            options={enumOptions(
                                Object.values(TeamLeaderboardVisibility)
                            )}
                            onChange={(value: TeamLeaderboardVisibility) =>
                                fields.leaderboardVisibility.onChange(value)
                            }
                        />
                    </div>
                </FormSection>

                <FormSection title="Optional">
                    <div className="grid gap-4 md:grid-cols-3">
                        <div className="flex flex-col gap-1.5">
                            <TextField
                                label="Member Limit"
                                type="number"
                                value={fields.memberLimitInput.value}
                                onChange={(value) =>
                                    fields.memberLimitInput.onChange(
                                        String(value)
                                    )
                                }
                                error={fields.memberLimitInput.error}
                            />
                            {!fields.memberLimitInput.error && (
                                <Hint>Leave empty for no limit</Hint>
                            )}
                        </div>
                    </div>
                </FormSection>

                {createRequest.error && (
                    <Alert variant="destructive">
                        <AlertDescription>
                            {extractApiErrorMessage(
                                createRequest.error,
                                'Failed to create team'
                            )}
                        </AlertDescription>
                    </Alert>
                )}
            </div>

            <StickyBar>
                <Button
                    variant="ghost"
                    onClick={onCancelHandler}
                    disabled={createRequest.loading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onSubmitHandler}
                    disabled={submitDisabled}
                    loading={createRequest.loading}
                >
                    Create Team
                </Button>
            </StickyBar>
        </>
    );
});

const InvalidCommunity = ({ message }: { message: string }) => (
    <Alert variant="destructive">
        <AlertDescription>{message}</AlertDescription>
    </Alert>
);

export const CreateTeam: React.FC = () => {
    const { communityId } = useParams();

    if (!communityId) {
        return <InvalidCommunity message="Missing community identifier." />;
    }

    const parsed = Number(communityId);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        return <InvalidCommunity message="Invalid community identifier." />;
    }

    return <CreateTeamForm communityId={parsed} />;
};
